"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function ThePathForward() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const steps = [{
    number: "01",
    title: "Initial Consultation",
    description: "We begin by understanding your unique property goals. Our team conducts a thorough, respectful assessment of your home to determine how we can provide the most value and a seamless transition.",
    detail: "PHASE: DISCOVERY"
  }, {
    number: "02",
    title: "Property Evaluation",
    description: "Our experts analyze the structural and market potential of your residence. We focus on long-term viability and responsible improvements that honor the character of the neighborhood.",
    detail: "PHASE: ANALYSIS"
  }, {
    number: "03",
    title: "Strategic Proposal",
    description: "We present a transparent, fair-market offer tailored to your timeline. Our commitment is to integrity and clarity, ensuring you feel confident in every decision made.",
    detail: "PHASE: PROPOSAL"
  }, {
    number: "04",
    title: "Seamless Closing",
    description: "We handle the complexities of the transaction with precision. Our streamlined process ensures a professional, stress-free experience from the initial agreement to the final signature.",
    detail: "PHASE: EXECUTION"
  }, {
    number: "05",
    title: "Legacy Stewardship",
    description: "Once acquired, we invest in thoughtful renovations and sustainable management. We are dedicated to preserving the value of your property for years to come.",
    detail: "PHASE: STEWARDSHIP"
  }];
  return <section data-section-id="2658" id="the-path-forward" className="dark bg-background text-foreground py-32 md:py-44 lg:py-[180px] px-6 md:px-10 lg:px-16 relative overflow-hidden">
      <div ref={headerRef} className={`max-w-5xl mx-auto text-center mb-20 md:mb-28 transition-all duration-700 ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        <p className="font-mono text-xs md:text-sm uppercase tracking-widest text-secondary mb-6">
          [ Our Investment Philosophy ]
        </p>
        <h2 className="font-default font-bold uppercase text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl leading-[0.95] tracking-tight">
          <span className="block text-foreground">The Path</span>
          <span className="block text-secondary">Forward</span>
        </h2>
      </div>

      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8">
        <div className="lg:col-span-8 relative">
          <div className="relative">
            <div className="absolute left-3 md:left-4 top-0 bottom-0 w-[2px] bg-secondary">
              <span className="absolute left-1/2 -translate-x-1/2 top-0 w-[2px] h-16 bg-gradient-to-b from-transparent via-secondary to-transparent opacity-90 animate-[brewpulse_3s_ease-in-out_infinite]" />
            </div>

            {steps.map((step, i) => {
            return <TimelineStep key={i} data-index={i} index={i} step={step} isLast={i === steps.length - 1} />;
          })}
          </div>
        </div>

        <div className="hidden lg:block lg:col-span-4">
          <div className="sticky top-24">
            <div className="relative overflow-hidden shadow-lg border border-border bg-card">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723050/img/residential_investment_property.png" alt="Modern residential property with warm golden hour lighting" loading="lazy" decoding="async" className="w-full h-[720px] object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent pointer-events-none" />
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <p className="font-mono text-[10px] uppercase tracking-widest text-secondary mb-2">
                  [ Property Portfolio / 2024 ]
                </p>
                <p className="font-mono text-xs text-foreground/70 uppercase">
                  Summertime Lane — Residential Stewardship
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes brewpulse {
          0% { transform: translate(-50%, 0); opacity: 0; }
          10% { opacity: 1; }
          90% { opacity: 1; }
          100% { transform: translate(-50%, 100vh); opacity: 0; }
        }
      `}</style>
    </section>;
}
function TimelineStep({
  step,
  index,
  isLast,
  ...rest
}: {
  step: {
    number: string;
    title: string;
    description: string;
    detail: string;
  };
  index: number;
  isLast: boolean;
  [key: string]: unknown;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  return <div ref={ref} {...rest} className={`relative pl-12 md:pl-20 ${isLast ? "pb-0" : "pb-12 md:pb-16"} min-h-[200px] transition-all duration-700 ${isVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-6"}`} style={{
    transitionDelay: `${index * 100}ms`
  }}>
      <div className="absolute left-0 top-2 w-6 h-6 md:w-7 md:h-7 bg-secondary border-2 border-background shadow-md z-10">
        <div className="absolute inset-1 bg-secondary/40" />
      </div>

      <div className="mb-3 md:mb-4">
        <span className="font-default font-bold text-secondary text-5xl md:text-6xl lg:text-7xl leading-none tracking-tighter">
          {step.number}
        </span>
      </div>

      <div className="bg-card backdrop-blur-xl border border-border shadow-sm p-6 md:p-8 max-w-xl">
        <h3 className="font-default font-bold uppercase text-xl md:text-2xl lg:text-[1.75rem] tracking-tight text-foreground mb-4 leading-tight">
          {step.title}
        </h3>
        <p className="font-default text-base md:text-base text-foreground/70 leading-relaxed mb-6 line-clamp-3">
          {step.description}
        </p>
        <div className="flex items-center gap-3 pt-4 border-t border-border">
          <span className="inline-block w-2 h-2 bg-secondary" />
          <p className="font-mono text-[11px] md:text-xs uppercase tracking-widest text-muted-foreground">
            {step.detail}
          </p>
        </div>
      </div>
    </div>;
}